import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
} from "react";
import ROLES from "../roles";
import { formatDate } from "../../formatting/date";
import { useTracking, EVENTS } from "../../Tracking";
import { getUrls } from "../routing/ContractUrls";
import {
  ButtonLink,
  ButtonLinkProps,
  ButtonLinkVariant,
} from "@teamscheme/button-link";
import { ReactNode } from "react";
import { ContractViewModel, Role } from "../model/ContractViewModel";
import { TrackEventProps } from "@teamscheme/tracking";
import { LocationDescriptor } from "history";
import styled from "styled-components";
import { ComponentType } from "react";
import { TextLink } from "@teamscheme/text-link";
import DeleteIcon from "@material-ui/icons/DeleteOutlineOutlined";
import {
  BankIdWrapper,
  ButtonLinkAndBankIDWrapper,
  SignedWithBankIdStyled,
} from "./CreateContractCard";

function loadSavedContracts() {
  try {
    const savedRaw = localStorage.getItem("SAVED_CONTRACTS");
    const saved = savedRaw ? JSON.parse(savedRaw) : { contracts: [] };
    return saved.contracts;
  } catch (error) {
    return [];
  }
}

export interface SavedContract {
  licensePlateNumber?: string;
  params: {
    id: string;
    token: { buyerToken: string | null; sellerToken: string | null };
  };
  created: string;
  role: Role;
  manufactureName?: string;
  modelName?: string;
}

export interface ISavedContractsContext {
  savedContracts: SavedContract[];
  hasLoaded: boolean;
  isLoading: boolean;
  hasSavedContracts: boolean;
  remove(contract: SavedContract): void;
  save(contract: ContractViewModel): void;
}

export const SavedContractsContext =
  React.createContext<ISavedContractsContext | null>(null);

export function useSavedContractsContext() {
  const savedContractsContext = useContext(SavedContractsContext);
  if (!savedContractsContext) {
    throw new Error("No SavedContractsContext found!");
  }
  return savedContractsContext;
}

export function SavedContractsProvider({ children }: { children: ReactNode }) {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [savedContracts, setSavedContracts] = useState<SavedContract[]>([]);

  useEffect(() => {
    setSavedContracts(loadSavedContracts());
    setIsLoading(false);
    setHasLoaded(true);
  }, []);

  const remove = useCallback(async function remove(contract) {
    removeContract(contract);
    setSavedContracts((x) =>
      x.filter(
        (x) =>
          !x.params ||
          x.params.id !== contract.params.id ||
          x.role !== contract.role
      )
    );
  }, []);

  const save = useCallback(function save(contract) {
    const result = saveContract(contract);
    if (result) setSavedContracts((x) => [...x, result]);
  }, []);

  const x = useMemo(() => {
    return {
      savedContracts,
      isLoading,
      hasLoaded,
      hasSavedContracts: !!savedContracts.length,
      remove,
      save,
    };
  }, [savedContracts, isLoading, remove, save, hasLoaded]);

  return (
    <SavedContractsContext.Provider value={x}>
      {children}
    </SavedContractsContext.Provider>
  );
}

function saveContract(contract: ContractViewModel): SavedContract | null {
  const { licensePlateNumber, created, id, vehicleData } = contract;

  const buyerToken = "buyerToken" in contract ? contract.buyerToken : null;
  const sellerToken = "sellerToken" in contract ? contract.sellerToken : null;

  const params = {
    id,
    token: { buyerToken, sellerToken },
  };

  const { manufactureName, modelName } = vehicleData || {};

  const role = buyerToken ? ROLES.BUYER : ROLES.SELLER;

  const added: SavedContract = {
    licensePlateNumber,
    params,
    created,
    role,
    manufactureName,
    modelName,
  };

  const existing = [...loadSavedContracts()];
  const match = existing.filter(
    (x) => x.params && x.params.id === added.params.id && x.role === added.role
  );

  if (match.length) return null;

  try {
    localStorage.setItem(
      "SAVED_CONTRACTS",
      JSON.stringify({
        contracts: [...existing, added],
      })
    );
  } catch (error) {
    console.log("Failed to save contract in local storage");

    return null;
  }

  return added;
}

function removeContract(contract: SavedContract) {
  const {
    params: { id },
    role,
  } = contract;

  localStorage.setItem(
    "SAVED_CONTRACTS",
    JSON.stringify({
      contracts: [...loadSavedContracts()].filter(
        (x) => !(x.params.id === id && x.role === role)
      ),
    })
  );
}

function getValues<T>(obj: { [s: string]: T }): T[] {
  return Object.keys(obj).map((k) => obj[k]);
}

export function mapContracts(savedContracts: SavedContract[]) {
  return getValues(
    savedContracts
      .sort((a, b) => +new Date(b.created) - +new Date(a.created))
      .map((contract) => ({
        contract,
        key: `${contract.params.id}-${contract.role}-${contract.created}`,
      }))
      .reduce((acc, c) => {
        acc[c.key] = c;
        return acc;
      }, {} as { [key: string]: { key: string; contract: SavedContract } })
  ).map(({ contract, key }, i) => {
    const { licensePlateNumber, params, created, role } = contract;
    const { token } = params;

    const urls = getUrls({
      token:
        typeof token === "object"
          ? token.buyerToken || token.sellerToken
          : token,
    });

    return {
      contract,
      contractUrl: urls.contract,
      licensePlateNumber,
      created,
      role,
      key,
    };
  });
}

const ContractLinkCreatedAndRoleWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing.space8};
  color: ${({ theme }) => theme.nafColor.neutral.neutral4};
  ${({ theme }) => theme.fontStyle.ui.small}
`;

const ContractLinkWrapper = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.space12} 0;
`;

export function ContractLink({
  contractUrl,
  role,
  title,
  created,
  trackEvent,
}: {
  contractUrl: LocationDescriptor;
  role: Role;
  title: string;
  created: string;
  trackEvent?: TrackEventProps;
}) {
  const tracking = useTracking();
  function onClick() {
    const base: TrackEventProps =
      trackEvent || EVENTS.LANDING_PAGE.OPEN_SAVED_CONTRACT;
    tracking.event({
      ...base,
      label: role === ROLES.SELLER ? "Seller" : "Buyer",
    });
  }

  const props = { onClick, linkTo: contractUrl };

  return (
    <ContractLinkWrapper>
      <ButtonLink variant="secondary" {...props}>
        {title}
      </ButtonLink>
      <ContractLinkCreatedAndRoleWrapper>
        <span>{formatDate(created)}</span> •{" "}
        <span>Du er {role === ROLES.BUYER ? "kjøper" : "selger"}</span>
      </ContractLinkCreatedAndRoleWrapper>
    </ContractLinkWrapper>
  );
}
const ListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.nafColor.neutral.neutral2};
`;

const SavedContractsWrapper = styled.div``;

const StyledTextLink = styled(TextLink)`
  color: ${({ theme }) => theme.nafColor.neutral.neutral4};
  display: flex;
`;

function SavedContractsList({
  savedContracts,
  onRemove,
}: {
  savedContracts: SavedContract[];
  onRemove(contract: SavedContract): void;
}) {
  return (
    <SavedContractsWrapper>
      {mapContracts(savedContracts).map(
        ({ contract, contractUrl, licensePlateNumber, created, role, key }) => {
          const { manufactureName, modelName } = contract || {};
          return (
            <ListItem key={`${licensePlateNumber}-${created}`}>
              <ContractLink
                contractUrl={contractUrl}
                role={role}
                title={
                  licensePlateNumber ||
                  [manufactureName, modelName].filter((p) => !!p).join(" ")
                }
                created={created}
              />
              <StyledTextLink onClick={() => onRemove(contract)}>
                <DeleteIcon />
              </StyledTextLink>
            </ListItem>
          );
        }
      )}
    </SavedContractsWrapper>
  );
}

const StartNewWrapper = styled.div``;

const InlineButtonLink = styled(ButtonLink)`
  display: inline-block;
` as ComponentType<ButtonLinkProps>;

export default function SavedContracts({
  savedContracts,
  showStartNew,
  onStartNew,
  onRemove,
}: {
  savedContracts: SavedContract[];
  showStartNew?: boolean;
  onStartNew?: () => void;
  onRemove(contract: SavedContract): void;
}) {
  const variant: ButtonLinkVariant = "primary";
  const startNewBaseProps = {
    variant,
    trackEvent: EVENTS.LANDING_PAGE.START_NEW,
    children: "Start en ny kontrakt",
  };
  const startNewButtonProps: ButtonLinkProps = onStartNew
    ? { ...startNewBaseProps, onClick: onStartNew }
    : { ...startNewBaseProps, linkTo: "/opprett" };
  return (
    <section className="SavedContracts">
      <h2>Fortsett der du slapp:</h2>
      {savedContracts.length ? (
        <div>
          <SavedContractsList
            savedContracts={savedContracts}
            onRemove={onRemove}
          />
          {showStartNew ? (
            <ButtonLinkAndBankIDWrapper>
              <StartNewWrapper>
                Eller{" "}
                <span>
                  <InlineButtonLink {...startNewButtonProps} />
                </span>
                .
              </StartNewWrapper>
              <SignedWithBankIdStyled />
            </ButtonLinkAndBankIDWrapper>
          ) : (
            <BankIdWrapper>
              <SignedWithBankIdStyled />
            </BankIdWrapper>
          )}
        </div>
      ) : (
        <p>Dine påbegynte kontrakter vil dukke opp her.</p>
      )}
    </section>
  );
}
